import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, Card, Skeleton, StatusMessage } from '@aimsos/ui';

import { WidgetPlayground } from '../components';
import { useWidgetForEdit } from '../hooks';

import { SearchX, TriangleAlert } from 'lucide-react';

/** Column skeletons, shaped like the form they replace, not a page spinner. */
function EditLoadingState() {
  return (
    <div className="flex min-w-0 flex-col gap-6 lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <Skeleton height={36} borderRadius={12} />
        <Card className="flex flex-col gap-3 p-6">
          <Skeleton height={20} width="40%" />
          <Skeleton height={64} />
          <Skeleton height={64} />
        </Card>
      </div>
      <div className="w-full shrink-0 lg:w-[380px] xl:w-[420px]">
        <Card className="flex flex-col gap-3 p-6">
          <Skeleton height={14} width="50%" />
          <Skeleton height={160} />
        </Card>
      </div>
    </div>
  );
}

/**
 * Widget Playground, edit mode (CT-5190).
 *
 * Hydration goes through `getWidget` (DS-6). The form is mounted **only once the
 * widget has arrived** — never against a half-loaded object, or `isDirty` would
 * compare the user's edits to the wrong snapshot and the discard prompt would
 * fire on an untouched form.
 *
 * A 404 and a failed envelope are different states with different exits: the
 * first offers the way back to the catalog, the second a retry.
 */
function WidgetBuilderEditPage() {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate();
  const { widgetId } = useParams();
  const { widget, isLoading, error, notFound, retry } = useWidgetForEdit(widgetId);

  const handleBackToCatalog = useCallback(() => {
    navigate('../..', { relative: 'path' });
  }, [navigate]);

  if (isLoading) {
    return (
      <main className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
          <EditLoadingState />
        </div>
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6">
          <StatusMessage
            icon={SearchX}
            title={t('widgetBuilder.edit.notFoundTitle')}
            subtitle={t('widgetBuilder.edit.notFoundSubtitle')}
          />
          <Button variant="secondary" onClick={handleBackToCatalog}>
            {t('widgetBuilder.edit.backToCatalog')}
          </Button>
        </div>
      </main>
    );
  }

  if (error || !widget) {
    return (
      <main className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6">
          <StatusMessage
            icon={TriangleAlert}
            title={t('widgetBuilder.edit.errorTitle')}
            subtitle={t('widgetBuilder.edit.errorSubtitle')}
          />
          <Button variant="secondary" onClick={retry}>
            {t('widgetBuilder.edit.retry')}
          </Button>
        </div>
      </main>
    );
  }

  return <WidgetPlayground mode="edit" initialWidget={widget} />;
}

export default WidgetBuilderEditPage;
